/**
 * ADR-092 and v3 page 1 "Attachments": which files the composer admits, and what it says about
 * them. The server decides the type again from the bytes; these checks only save a wasted upload.
 */
export const ATTACHMENT_ACCEPT = "image/*,.pdf,.csv,.xlsx,.xls,.txt,.docx";
export const MAX_ATTACHMENTS = 5;
export const MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024;

const XLSX = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const DOCX = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

const BY_EXTENSION: Readonly<Record<string, string>> = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  gif: "image/gif",
  pdf: "application/pdf",
  csv: "text/csv",
  txt: "text/plain",
  xlsx: XLSX,
  docx: DOCX,
};

const IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/webp", "image/gif"]);

export interface FileLike {
  readonly name: string;
  readonly type: string;
  readonly size: number;
}

export type Admission =
  | { readonly ok: true; readonly mediaType: string; readonly photo: boolean }
  | { readonly ok: false; readonly error: string };

/** The declared type the server will check the bytes against, or why the file is not accepted. */
export function admitFile(file: FileLike): Admission {
  const extension = file.name.includes(".") ? file.name.split(".").pop()!.toLowerCase() : "";
  if (extension === "xls" || extension === "doc") {
    return {
      ok: false,
      error: `${file.name} is an older Excel or Word file. Save it as .xlsx or .docx and attach it again.`,
    };
  }
  if (extension === "heic" || extension === "heif" || /image\/hei[cf]/.test(file.type)) {
    return {
      ok: false,
      error: `${file.name} is a HEIC photo. Save it as JPEG or PNG and attach it again.`,
    };
  }
  // A pasted image has no useful name, so a photo's type is also taken from the browser.
  const mediaType = BY_EXTENSION[extension] ?? (IMAGE_TYPES.has(file.type) ? file.type : undefined);
  if (mediaType === undefined) {
    return {
      ok: false,
      error: `${file.name} is not a supported type. Use a photo, PDF, CSV, Excel, Word or text file.`,
    };
  }
  if (file.size === 0) return { ok: false, error: `${file.name} is empty.` };
  if (file.size > MAX_ATTACHMENT_BYTES)
    return { ok: false, error: `${file.name} is over the 20 MB limit.` };
  return { ok: true, mediaType, photo: IMAGE_TYPES.has(mediaType) };
}

/** Admits files up to the per-message limit and says what was left out and why. */
export function admitFiles<T extends FileLike>(
  alreadyAttached: number,
  files: readonly T[],
): {
  readonly admitted: readonly { file: T; mediaType: string; photo: boolean }[];
  readonly errors: readonly string[];
} {
  const admitted: { file: T; mediaType: string; photo: boolean }[] = [];
  const errors: string[] = [];
  let overLimit = 0;
  for (const file of files) {
    const admission = admitFile(file);
    if (!admission.ok) {
      errors.push(admission.error);
      continue;
    }
    if (alreadyAttached + admitted.length >= MAX_ATTACHMENTS) {
      overLimit += 1;
      continue;
    }
    admitted.push({ file, mediaType: admission.mediaType, photo: admission.photo });
  }
  if (overLimit > 0)
    errors.push(`Up to ${MAX_ATTACHMENTS} files per message. ${overLimit} not added.`);
  return { admitted, errors };
}

export type ComposerAttachmentState = "uploading" | "reading" | "ready" | "failed";

export interface ComposerAttachmentView {
  readonly state: ComposerAttachmentState;
}

/** Why Send is blocked by the attached files, in the design's words, or undefined when it is not. */
export function attachmentSendBlock(items: readonly ComposerAttachmentView[]): string | undefined {
  if (items.some((item) => item.state === "failed")) {
    return "One file did not upload. Retry it or remove it.";
  }
  if (items.some((item) => item.state === "uploading" || item.state === "reading")) {
    return "Wait for the upload to finish, then send.";
  }
  return undefined;
}

/** The question sent when there are files but no words. */
export function defaultAttachmentQuestion(photosOnly: boolean): string {
  return photosOnly
    ? "What does this show for my portfolio?"
    : "Read this and tell me what matters for my portfolio.";
}

export function formatAttachmentSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function fileExtension(name: string): string {
  return name.includes(".") ? name.split(".").pop()!.toUpperCase().slice(0, 4) : "FILE";
}
